import React, { Component } from 'react';
import * as addressTools from '@simplybenefits/sb-address';
import * as dates from '@simplybenefits/sb-dates';
import { Add as AddIcon } from '@mui/icons-material';
import Text from '../../components/Text.js';
import TextInput from '../../components/TextFields/TextInput.js';
import { DateInput, EditBankingDetails, localizationMaps } from '../../index.js';
import Button from '../../components/Button.js';
import Modal from '../../components/Modals/Modal.js';
import * as Services from '../../services/index.js';
import { globalStyles } from '../../theme/index.js';
import EntityTable from './EntityTable.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import DisplayBankingDetails from '../../components/DisplayBankingDetails.js';
class DivisionsManagement extends Component {
  state = {
    showNewUser: false,
    currentUser: '',
    showNewDivision: false,
    title: '',
    description: '',
    suite: '',
    firstLine: '',
    city: '',
    province: 'Alberta',
    postalCode: '',
    country: 'Canada',
    selectedDivision: '',
    isDivisionEdit: false,
    eftDetails: null,
    showRemoveModal: false,
    confirmModalOpen: false,
    signage: {
      name: '',
      signature: '',
      acceptedTerms: false,
      timestampOfAcceptance: null,
      datedOn: null
    }
  };
  signageRef = /*#__PURE__*/React.createRef();
  accountDetailsRef = /*#__PURE__*/React.createRef();
  componentDidUpdate = (prevProps, prevState) => {
    if (this.signageRef.current && prevState.eftDetails !== this.state.eftDetails) {
      this.signageRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  _toggleRemoveDivision = () => {
    this.setState({
      showRemoveModal: !this.state.showRemoveModal
    });
  };
  _toggleAddDivision = () => {
    this.setState({
      showNewDivision: !this.state.showNewDivision,
      title: '',
      description: '',
      suite: '',
      firstLine: '',
      city: '',
      province: 'Alberta',
      postalCode: '',
      isDivisionEdit: false,
      eftDetails: null,
      showBankingModal: false,
      hasBanking: false,
      signage: {
        name: '',
        signature: '',
        acceptedTerms: false,
        timestampOfAcceptance: null,
        datedOn: null
      }
    });
  };
  _showConfirmDialog = () => {
    this.setState({
      confirmModalOpen: !this.state.confirmModalOpen
    });
  };
  _handleSignageChange = (type, value) => {
    if (type === 'acceptedTerms' && value === true) {
      this.setState({
        signage: {
          ...this.state.signage,
          acceptedTerms: true,
          timestampOfAcceptance: dates.getUTC()
        }
      });
      return null;
    }
    this.setState({
      signage: {
        ...this.state.signage,
        [type]: value
      }
    });
    return null;
  };
  _validateForm = async () => {
    if (this.state.eftDetails !== null) {
      const bankingIsValid = this.state.eftDetails.default.accepted;
      if (!bankingIsValid) {
        return false;
      }
      const signageComplete = await this._isSignageCompleted();
      if (!signageComplete) {
        alert(i18n.t(tokens.labels.banking.provideSignatureToContinue));
        return false;
      }
    }
    let titleError = await Services.validateInput('divisionName', this.state.title);
    let descriptionError = await Services.validateInput('checkEmpty', this.state.description);
    const firstLineError = await Services.validateInput('firstLine', this.state.firstLine);
    const cityError = await Services.validateInput('city', this.state.city);
    const provinceError = await Services.validateInput('province', this.state.province);
    const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);
    if (this.state.title.length < 1) {
      titleError = i18n.t(tokens.labels.classesAndDivisions.divisionTitleRequired);
    } else if (this.state.title.length > 20) {
      titleError = i18n.t(tokens.labels.classesAndDivisions.divisionTitleLength);
    }
    if (this.state.description && this.state.description.length > 200) {
      descriptionError = i18n.t(tokens.labels.classesAndDivisions.descriptionLengthDivision);
    }
    this.setState({
      titleError,
      descriptionError,
      firstLineError,
      cityError,
      provinceError,
      postalCodeError
    });
    if (titleError || descriptionError || firstLineError || cityError || provinceError || postalCodeError) {
      this.accountDetailsRef.current && this.accountDetailsRef.current.scrollIntoView({
        behavior: 'smooth'
      });
      return false;
    }
    return true;
  };
  _isSignageCompleted = async () => {
    const {
      name,
      signature,
      datedOn
    } = this.state.signage;
    let signageNameError = await Services.validateInput('checkEmpty', name);
    const signageSignatureError = await Services.validateInput('checkEmpty', signature);
    const signageDatedOnError = await Services.validateInput('date', datedOn, i18n.locale);
    const signageAcceptedError = !this.state.eftDetails.default.accepted;
    if (name !== signature) {
      signageNameError = i18n.t(tokens.labels.signage.signageNameError);
    }
    if (signageAcceptedError) {
      alert(i18n.t(tokens.labels.signage.pleaseAcceptAndAcknowledgeChanges));
    }
    this.setState({
      signageNameError,
      signageSignatureError,
      signageDatedOnError
    });
    if (signageNameError || signageSignatureError || signageDatedOnError || signageAcceptedError) {
      return false;
    }
    return true;
  };
  _addDivision = async () => {
    const isValid = await this._validateForm();
    if (!isValid) {
      return;
    }
    let bankingDetails = null;
    if (this.state.eftDetails !== null) {
      bankingDetails = {
        accountHolder: this.state.eftDetails.default.accountHolder,
        institutionName: this.state.eftDetails.default.institutionName,
        institutionAddress: this.state.eftDetails.default.institutionAddress,
        institutionNum: this.state.eftDetails.default.institutionNum,
        transitNum: this.state.eftDetails.default.transitNum,
        accountNum: this.state.eftDetails.default.accountNum,
        accepted: this.state.eftDetails.default.accepted,
        timestampOfAcceptance: this.state.eftDetails.default.timestampOfAcceptance,
        signage: {
          name: this.state.signage.name.trim(),
          signature: this.state.signage.signature.trim(),
          acceptedTerms: this.state.signage.acceptedTerms,
          timestampOfAcceptance: this.state.signage.timestampOfAcceptance,
          datedOn: Services.dates.formatOutgoingDate(this.state.signage.datedOn)
        }
      };
    }
    const {
      suite,
      firstLine,
      city,
      postalCode,
      country
    } = this.state;
    const province = localizationMaps.toEnglish("provinces", this.state.province);
    const generatedAddress = addressTools.generateAddress({
      suite,
      firstLine,
      city,
      province,
      postalCode,
      country
    });
    await this.props.addDivision(this.state.title.trim(), this.state.description.trim(), generatedAddress.trim(), bankingDetails, this.props.currentAccountMeta ? this.props.currentAccountMeta.accountID : null);
    this._toggleAddDivision();
  };
  _editDivision = async () => {
    const isValid = await this._validateForm();
    if (!isValid) {
      return;
    }
    let bankingDetails = null;
    if (this.state.eftDetails !== null) {
      bankingDetails = {
        accountHolder: this.state.eftDetails.default.accountHolder,
        institutionName: this.state.eftDetails.default.institutionName,
        institutionAddress: this.state.eftDetails.default.institutionAddress,
        institutionNum: this.state.eftDetails.default.institutionNum,
        transitNum: this.state.eftDetails.default.transitNum,
        accountNum: this.state.eftDetails.default.accountNum,
        accepted: this.state.eftDetails.default.accepted,
        timestampOfAcceptance: this.state.eftDetails.default.timestampOfAcceptance,
        signage: {
          name: this.state.signage.name.trim(),
          signature: this.state.signage.signature.trim(),
          acceptedTerms: this.state.signage.acceptedTerms,
          timestampOfAcceptance: this.state.signage.timestampOfAcceptance,
          datedOn: Services.dates.formatOutgoingDate(this.state.signage.datedOn)
        }
      };
    }
    const {
      suite,
      firstLine,
      city,
      postalCode,
      country
    } = this.state;
    const province = localizationMaps.toEnglish("provinces", this.state.province);
    const generatedAddress = addressTools.generateAddress({
      suite,
      firstLine,
      city,
      province,
      postalCode,
      country
    });
    await this.props.editDivision(this.state.title.trim(), this.state.description.trim(), generatedAddress.trim(), bankingDetails, this.props.currentAccountMeta ? this.props.currentAccountMeta.accountID : null, this.state.selectedDivision, this.state.hasBanking);
    this._toggleAddDivision();
  };
  _openRemoveDivision = division => {
    this.setState({
      showRemoveModal: true,
      title: division.title.trim(),
      description: division.description.trim(),
      selectedDivision: division.divisionID
    });
  };
  _removeDivision = async () => {
    const success = await this.props.removeDivision(this.state.selectedDivision);
    if (success) {
      this.setState({
        showRemoveModal: false,
        selectedDivision: null
      });
    }
  };
  _openEditDivision = division => {
    let address = '';
    if (division.address) {
      address = addressTools.parseAddress(division.address);
    }
    this.setState({
      showNewDivision: true,
      title: division.title,
      description: division.description,
      suite: address.suite,
      firstLine: address.firstLine,
      city: address.city,
      province: address.province,
      postalCode: address.postalCode,
      selectedDivision: division.divisionID,
      hasBanking: division.hasBanking,
      isDivisionEdit: true
    });
  };
  _onBlur = async type => {
    this.setState({
      [`${type}Error`]: await Services.validateInput(type, this.state[type])
    });
  };
  _onAddressBlur = async type => {
    if (type !== 'suite') {
      await this.setState({
        [`${type}Error`]: await Services.validateInput(type, this.state[type])
      });
    }
    const {
      firstLine,
      city,
      province,
      postalCode
    } = this.state;
    if (firstLine && city && province && postalCode) {
      await addressTools.generateAddress(this.state);
    }
    return null;
  };
  _handleInputChange = (type, event, isValue) => {
    const value = isValue ? event : event.target.value;
    if (type === 'accepted' && value === true) {
      this.setState({
        accepted: value,
        acceptedError: false,
        timestampOfAcceptance: dates.getUTC()
      });
    } else {
      this.setState({
        [type]: value
      });
    }
  };
  _handleAddressChange = (type, event) => {
    this.setState({
      [type]: event.target.value.replace(/,/g, '')
    });
  };
  _saveNewBankingDetails = newBankingDetails => {
    this.setState({
      ...this.state,
      eftDetails: {
        ...newBankingDetails
      },
      hasBanking: true,
      showBankingModal: false
    });
  };
  _toggleBankingDetailsModal = () => {
    this.setState({
      showBankingModal: !this.state.showBankingModal
    });
  };
  render() {
    const {
      suite,
      firstLine,
      city,
      province,
      postalCode
    } = this.state;
    const styles = {
      modalFlex: {
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column'
      },
      customContainerStyle: {
        // minHeight: 400,
        maxWidth: "500px"
      },
      contentBlock: globalStyles.contentBlock,
      contentMargin: globalStyles.contentMargin,
      form: globalStyles.form,
      title: globalStyles.title,
      titleBlock: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px'
      },
      row: {
        display: 'flex',
        flexDirection: 'row'
      },
      divider: {
        width: '40px'
      }
    };
    const isPrivateLabel = this.props.currentAccountMeta.isPrivateLabel;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: '50px'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: styles.titleBlock
    }, /*#__PURE__*/React.createElement(Text, {
      large: true,
      bold: true
    }, i18n.t(tokens.labels.admin.divisions)), (this.props.step <= 3 || this.props.isMasterSite || isPrivateLabel) && /*#__PURE__*/React.createElement(Button, {
      transparent: true,
      onClick: this._toggleAddDivision,
      customStyle: {
        marginLeft: 'auto'
      }
    }, /*#__PURE__*/React.createElement(AddIcon, {
      style: {
        fontSize: 17,
        lineHeight: '14px',
        color: 'inherit'
      }
    }), "\xA0", i18n.t(tokens.buttons.createDivision))), /*#__PURE__*/React.createElement(EntityTable, {
      items: this.props.divisions,
      edit: !(this.props.isMasterSite || isPrivateLabel) && this.props.step >= 4 ? null : this._openEditDivision,
      remove: !this.props.isQuote ? null : !(this.props.isMasterSite || isPrivateLabel) && this.props.step >= 4 ? null : this._openRemoveDivision,
      language: this.props.language || "en"
    }), /*#__PURE__*/React.createElement(Modal, {
      open: this.state.showRemoveModal,
      onClose: this._toggleRemoveDivision,
      title: i18n.t(tokens.labels.classesAndDivisions.removeDivision),
      primaryButton: {
        label: i18n.t(tokens.labels.classesAndDivisions.removeDivision),
        onClick: this._removeDivision
      },
      loader: this.props.loader
    }, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      light: true
    }, i18n.t(tokens.labels.classesAndDivisions.removalConfirmationDivision), " ", /*#__PURE__*/React.createElement("b", null, this.state.title), i18n.t(tokens.labels.classesAndDivisions.allDataForDivisionLost))), /*#__PURE__*/React.createElement(Modal, {
      open: this.state.showNewDivision,
      onClose: this._toggleAddDivision,
      title: this.state.isDivisionEdit ? i18n.t(tokens.titles.editDivision) : i18n.t(tokens.titles.newDivision),
      primaryButton: {
        onClick: this.state.isDivisionEdit ? this._editDivision : this._addDivision
      },
      loader: this.props.loader
    }, /*#__PURE__*/React.createElement("div", {
      ref: this.accountDetailsRef
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.classesAndDivisions.title),
      value: this.state.title,
      onChange: value => this._handleInputChange('title', value),
      error: Boolean(this.state.titleError),
      helperText: this.state.titleError,
      fullWidth: true,
      autofocus: true
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.classesAndDivisions.description),
      value: this.state.description,
      onChange: value => this._handleInputChange('description', value),
      error: Boolean(this.state.descriptionError),
      helperText: this.state.descriptionError,
      fullWidth: true,
      autofocus: true
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.streetAddress),
      value: firstLine,
      onBlur: () => this._onAddressBlur('firstLine'),
      onChange: value => this._handleAddressChange('firstLine', value),
      error: Boolean(this.state.firstLineError),
      helperText: this.state.firstLineError,
      fullWidth: true
    }), /*#__PURE__*/React.createElement("div", {
      style: styles.row
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.account.suiteUnit),
      value: suite,
      onBlur: () => this._onAddressBlur('suite'),
      onChange: value => this._handleAddressChange('suite', value),
      fullWidth: true
    }), /*#__PURE__*/React.createElement("div", {
      style: styles.divider
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.account.cityTown),
      value: city,
      onBlur: () => this._onAddressBlur('city'),
      onChange: value => this._handleAddressChange('city', value),
      error: Boolean(this.state.cityError),
      helperText: this.state.cityError,
      fullWidth: true
    })), /*#__PURE__*/React.createElement("div", {
      style: styles.row
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.province),
      value: localizationMaps.fromEnglish("provinces", province) || 'Alberta',
      onBlur: () => this._onAddressBlur('province'),
      onChange: value => this._handleInputChange('province', value),
      select: true,
      selectOptions: Services.selectFieldOptions.provinceOptions(),
      fullWidth: true
    }), /*#__PURE__*/React.createElement("div", {
      style: styles.divider
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.postalCode),
      value: postalCode,
      onBlur: () => this._onAddressBlur('postalCode'),
      onChange: value => this._handleAddressChange('postalCode', value),
      error: Boolean(this.state.postalCodeError),
      helperText: this.state.postalCodeError,
      fullWidth: true
    }))), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this._toggleBankingDetailsModal(),
      customStyle: {
        width: "100%"
      },
      marginTop: "20px",
      marginBottom: "5px",
      transparent: true
    }, this.state.showBankingModal ? 'Close' : this.state.hasBanking || this.state.eftDetails ? i18n.t(tokens.labels.banking.override) : i18n.t(tokens.buttons.add), " ", i18n.t(tokens.buttons.bankingDetails)), this.state.eftDetails && /*#__PURE__*/React.createElement(DisplayBankingDetails, {
      accountHolder: this.state.eftDetails.default.accountHolder,
      accountNum: this.state.eftDetails.default.accountNum,
      institutionAddress: this.state.eftDetails.default.institutionAddress,
      institutionName: this.state.eftDetails.default.institutionName,
      institutionNum: this.state.eftDetails.default.institutionNum,
      transitNum: this.state.eftDetails.default.transitNum,
      accepted: this.state.eftDetails.default.accepted
    }), this.state.eftDetails?.default?.accepted ? /*#__PURE__*/React.createElement("div", {
      ref: this.signageRef
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.yourName),
      fullWidth: true,
      value: this.state.signage.name,
      onChange: e => this._handleSignageChange('name', e.target.value),
      error: this.state.signageNameError
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.signature),
      fullWidth: true,
      value: this.state.signage.signature,
      onChange: e => this._handleSignageChange('signature', e.target.value),
      isSignature: true,
      error: this.state.signageSignatureError
    }), /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.account.datedOnYYYYMMDD),
      fullWidth: true,
      value: this.state.signage.datedOn,
      onChange: e => this._handleSignageChange('datedOn', e.target.value),
      error: this.state.signageDatedOnError
    })) : null), this.state.showBankingModal && /*#__PURE__*/React.createElement(EditBankingDetails, {
      modalOpen: this.state.showBankingModal,
      validateBankingDetails: this.props.validateBankingDetails,
      bankingDetailsVerifying: this.props.bankingDetailsVerifying,
      whitelabel: this.props.whitelabel,
      saveNewBankingDetails: this._saveNewBankingDetails,
      accountLevelBankingDetailsView: true,
      close: () => this._toggleBankingDetailsModal(),
      loader: this.props.loader
    }), /*#__PURE__*/React.createElement("span", {
      id: "divisions-card"
    }));
  }
}
export default DivisionsManagement;